import * as Dialog from "@radix-ui/react-dialog";
import Button from "@/components/ui/button";

interface ModeSwitchConfirmationModalProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	fromMode: "dynamic" | "static";
	toMode: "dynamic" | "static";
	currentReviewCount: number;
	onConfirm: () => void;
}

export default function ModeSwitchConfirmationModal({ open, onOpenChange, fromMode, toMode, currentReviewCount: _currentReviewCount, onConfirm }: ModeSwitchConfirmationModalProps) {
	const isSwitchingToStatic = toMode === "static";

	const title = isSwitchingToStatic ? "Switch to Static mode?" : "Switch to Dynamic mode?";

	const confirmText = isSwitchingToStatic ? "Switch to Static" : "Switch to Dynamic";

	const handleConfirm = () => {
		onConfirm();
		onOpenChange(false);
	};

	return (
		<Dialog.Root open={open} onOpenChange={onOpenChange}>
			<Dialog.Portal>
				<Dialog.Overlay className="fixed inset-0 bg-black/50 z-50 modal-overlay" />
				<Dialog.Content className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-xl shadow-xl z-50 w-full max-w-lg p-6 modal-content">
					{/* Icon */}
					<div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 rounded-full bg-gray-100">
						<svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								strokeWidth={1.5}
								d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
							/>
						</svg>
					</div>

					{/* Title */}
					<Dialog.Title className="text-lg font-semibold text-gray-900 text-center mb-2">{title}</Dialog.Title>

					{/* Description */}
					{isSwitchingToStatic ? (
						<div className="mb-6">
							<Dialog.Description className="text-sm text-tertiary mb-2">Static mode gives you full control over which reviews appear.</Dialog.Description>
							<Dialog.Description className="text-sm text-tertiary">New testimonials won't be added automatically.</Dialog.Description>
						</div>
					) : (
						<div className="mb-6">
							<Dialog.Description className="text-sm text-tertiary mb-3">Dynamic walls update automatically when new testimonials match your filters.</Dialog.Description>
							<ul className="text-sm text-tertiary space-y-1">
								<li>• New approved testimonials will auto-appear</li>
								<li>• Manually curated selections will no longer apply</li>
							</ul>
						</div>
					)}

					{/* Divider */}
					<div className="flex items-center justify-center mb-6">
						<div className="w-full border-t border-gray-200"></div>
					</div>

					{/* Mode comparison */}
					<div className="flex items-center gap-3 mb-6">
						{/* From mode tile */}
						<div className={`flex-1 p-4 rounded-lg border ${fromMode === "dynamic" ? "border-gray-300 bg-gray-50" : "border-gray-300 bg-gray-50"}`}>
							<div className="flex items-center gap-2 mb-2">
								<span className="text-sm font-medium text-gray-900">{fromMode === "dynamic" ? "Dynamic" : "Static"}</span>
								<span className="text-xs text-gray-500">(current)</span>
							</div>
							{fromMode === "dynamic" ? (
								<>
									<p className="text-xs font-medium text-gray-700 mb-2">Auto-updating wall</p>
									<ul className="text-xs text-tertiary space-y-1">
										<li>• Reviews update automatically</li>
										<li>• Based on filters</li>
										<li>• No manual curation</li>
									</ul>
								</>
							) : (
								<>
									<p className="text-xs font-medium text-gray-700 mb-2">Manually curated wall</p>
									<ul className="text-xs text-tertiary space-y-1">
										<li>• Only selected reviews appear</li>
										<li>• No automatic updates</li>
										<li>• Full control over content</li>
									</ul>
								</>
							)}
						</div>
						<div className="shrink-0">
							<svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
							</svg>
						</div>
						{/* To mode tile */}
						<div className={`flex-1 p-4 rounded-lg border ${toMode === "dynamic" ? "border-gray-200" : "border-gray-200"}`}>
							<div className="flex items-center gap-2 mb-2">
								<span className="text-sm font-medium text-gray-900">{toMode === "dynamic" ? "Dynamic" : "Static"}</span>
							</div>
							{toMode === "dynamic" ? (
								<>
									<p className="text-xs font-medium text-gray-700 mb-2">Auto-updating wall</p>
									<ul className="text-xs text-tertiary space-y-1">
										<li>• Reviews update automatically</li>
										<li>• Based on filters</li>
										<li>• No manual curation</li>
									</ul>
								</>
							) : (
								<>
									<p className="text-xs font-medium text-gray-700 mb-2">Manually curated wall</p>
									<ul className="text-xs text-tertiary space-y-1">
										<li>• Only selected reviews appear</li>
										<li>• No automatic updates</li>
										<li>• Full control over content</li>
									</ul>
								</>
							)}
						</div>
					</div>

					{/* Divider */}
					<div className="flex items-center justify-center mb-6">
						<div className="w-full border-t border-gray-200"></div>
					</div>

					{/* Helper note */}
					{isSwitchingToStatic ? (
						<div className="flex items-start gap-2 mb-6">
							<svg className="w-5 h-5 text-gray-400 shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
							</svg>
							<p className="text-xs text-tertiary">
								Nothing will change immediately.
								<br />
								You can start selecting reviews after switching.
							</p>
						</div>
					) : (
						<div className="flex items-start gap-2 mb-6">
							<svg className="w-5 h-5 text-gray-400 shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
							</svg>
							<p className="text-xs text-tertiary">
								Nothing will change immediately.
								<br />
								You can set filter criteria to display reviews after switching.
							</p>
						</div>
					)}

					{/* Actions */}
					<div className="flex gap-3">
						<Button variant="soft" className="flex-1" onClick={() => onOpenChange(false)}>
							Cancel
						</Button>
						<Button className="flex-1" onClick={handleConfirm}>
							{confirmText}
						</Button>
					</div>
				</Dialog.Content>
			</Dialog.Portal>
		</Dialog.Root>
	);
}
